import { Box, useMediaQuery } from '@mui/material'
import { SurveyHeader } from '../SurveyComponents/SurveyHeader';
import { useSurveyContext } from '../Context/SurveyCustomHooks/useSurveyContext';
import { FooterBtn } from './FooterBtn';
import { SurveySkeleton } from './SurveySkeleton';
import { NotFound } from '../../../ErrorComponents/NotFound';
import { SurveyQuestions } from './SurveyQuestions/SurveyQuestions';
import MetaDecorator from '../../../UtilsComponents/MetaDecorator';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

export const SurveyContent = () => {
    const {
        surveyBackgroundColor: backgroundColor,
        isLoadingStyles,
        customer,
        animation,
        handleNext,
        handleBack,
        isErrorStyles,
        isLoadingQuestions,
        surveyInfo,
        isFetchingQuestions,
        isErrorQuestions,
        userId,
        surveyStyles,
        questionsNumber
    } = useSurveyContext();
    const navigate = useNavigate();

    //#region Control height and scroll
    // Check if the screen height is less than or equal to 550px. This threshold is used to identify short screens
    // where scrolling might be necessary to view all content.
    const isShortScreen = useMediaQuery('(max-height: 550px)');

    // Check if the screen height is greater than or equal to 600px. This threshold is used to disable scrolling
    // on taller screens where all content is likely visible without scrolling.
    const noScroll = useMediaQuery('(min-height: 600px)');

    const questionRef = useRef(null);
    const [questionHeight, setQuestionHeight] = useState(0);

    // Activate scrolling if the screen is short (isShortScreen) or if the height of the question content exceeds 200px.
    // The 200px threshold ensures that scrolling is enabled for larger question content even on medium-height screens.
    const activateScroll = (isShortScreen || questionHeight > 200) && !noScroll;

    useEffect(() => {
        if (!questionRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setQuestionHeight(entry.contentRect.height);
            }
        });
        observer.observe(questionRef.current);
        return () => observer.disconnect();
    }, []);

    /**
     * Handles mouse wheel scrolling to navigate between survey pages.
     * 
     * @param {WheelEvent} event - The wheel event object.
     * @description When the user scrolls up, it calls handleNext() to go to the next page.
     * When the user scrolls down, it calls handleBack() to go to the previous page.
     * The commented-out code suggests a throttling mechanism was planned but is currently disabled.
     */
    const handleScroll = (event) => {
        // let lastScrollTime = 0;
        // const now = Date.now();
        // if (now - lastScrollTime < 200) return; // Espera 500ms entre desplazamientos
        // lastScrollTime = now;
        const delta = event.deltaY; // Detecta la dirección del scroll
        if (delta < 0) {
            handleNext();
        } else if (delta > 0) {
            handleBack();
        }
    };
    //#endregion

    //#region Navigate to PostSurvey when survey is completed or status is 4
    useEffect(() => {
        if (surveyInfo && surveyInfo.status === "4") {
            navigate(
                `/postsurvey/${customer}/${userId}`,
                {
                    state: {
                        surveyInfo,
                        surveyStyles
                    }
                }
            );
        }
    }, [surveyInfo, customer, userId, navigate, surveyStyles]);
    //#endregion

    if ((isLoadingStyles || isLoadingQuestions || isFetchingQuestions || questionsNumber === undefined) && !isErrorQuestions) return <SurveySkeleton />
    if (isErrorStyles || isErrorQuestions) return <NotFound />
    if ([0, 1].includes(parseInt(surveyInfo.status))) {
        return <NotFound />
    }
    if (surveyInfo.status === "4") {
        // Return null to avoid rendering anything while navigating
        return null
    }
    return (
        <Box
            sx={{
                backgroundColor,
                height: "100vh",
                overflowY: activateScroll ? "scroll" : "hidden", // Scroll personalizado
                overflowX: "hidden",
            }}
            onWheel={!activateScroll ? handleScroll : undefined}
        >
            <MetaDecorator
                key={0}
                title={customer?.includes("20Mission".toLowerCase()) ? `Survey | 20Mission Cerveza` : `Survey | ${customer.split(" ").map(subStr => subStr[0].toUpperCase() + subStr.slice(1)).join(" ")}`}
                description={"Para nosotros es muy importante conocer tu opinión sobre nuestros productos y servicios. Por favor, ayúdanos a mejorar respondiendo esta encuesta."}
                imageUrl={"/images/7Bytes.svg"}
                imageAlt={"7Bytes"}
                path={window.location.pathname}
            />
            <SurveyHeader />
            <div
                ref={questionRef}
                className={`${animation}`}
                style={{
                    position: "relative",
                    width: "100%"
                }}
            >
                <SurveyQuestions />
            </div>
            {/* <Box sx={{ height: "200px" }} /> */}
            <FooterBtn />
        </Box>
    )
}
